import { Component } from '@theme/component';

/**
 * @typedef {Object} TextTruncateRefs
 * @property {HTMLElement} content - The wrapper around the text that gets clamped
 * @property {HTMLButtonElement} toggle - The "Ver mais / Ver menos" button
 */

/**
 * Clamps long text to a max height with a fade and a toggle button.
 * The button only shows when the content is actually taller than the limit.
 * Works inside accordions: content is re-measured whenever its size changes
 * (e.g. when the parent <details> opens).
 *
 * @extends {Component<TextTruncateRefs>}
 */
class TextTruncate extends Component {
  requiredRefs = ['content', 'toggle'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();

    this.#observer = new ResizeObserver(() => this.#measure());
    this.#observer.observe(this.refs.content);

    // Re-measure when a parent <details> toggles (accordion rows)
    const details = this.closest('details');
    if (details) details.addEventListener('toggle', this.#measure);

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    const details = this.closest('details');
    if (details) details.removeEventListener('toggle', this.#measure);
  }

  get #maxHeight() {
    const value = getComputedStyle(this).getPropertyValue('--truncate-max-height');
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 220;
  }

  #measure = () => {
    if (this.#expanded) return;

    const { content } = this.refs;
    const inner = content.firstElementChild;
    if (!(inner instanceof HTMLElement)) return;

    // Hidden (e.g. closed accordion) — nothing to measure yet
    if (inner.offsetHeight === 0) return;

    // Small buffer so we don't show the button for a couple of pixels
    const isOverflowing = inner.scrollHeight > this.#maxHeight + 24;
    this.classList.toggle('is-truncated', isOverflowing);
  };

  /**
   * Toggles between clamped and fully expanded state.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    const { content, toggle } = this.refs;
    const startHeight = content.offsetHeight;

    this.#expanded = !this.#expanded;
    this.classList.toggle('is-expanded', this.#expanded);
    toggle.setAttribute('aria-expanded', String(this.#expanded));

    const endHeight = content.offsetHeight;

    if (typeof content.animate === 'function' && startHeight !== endHeight) {
      content.style.overflow = 'hidden';
      const animation = content.animate(
        { height: [`${startHeight}px`, `${endHeight}px`] },
        { duration: 300, easing: 'ease-in-out' }
      );
      animation.onfinish = () => {
        content.style.removeProperty('height');
        content.style.overflow = '';
      };
    }

    // When collapsing, bring the top of the text back into view
    if (!this.#expanded) {
      const top = this.getBoundingClientRect().top;
      if (top < 0) this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };
}

if (!customElements.get('text-truncate')) {
  customElements.define('text-truncate', TextTruncate);
}
